import { useState, useRef, useCallback } from 'react';
import ProofreadPanel from './components/ProofreadPanel';
import PlagiarismPanel from './components/PlagiarismPanel';
import CitationPanel from './components/CitationPanel';
import ReferenceBuilderPanel from './components/ReferenceBuilderPanel';
import ReadabilityPanel from './components/ReadabilityPanel';
import OutlineBuilderModal from './components/OutlineBuilderModal';
import TemplatesTab from './components/TemplatesTab';
import { usePaperWriter, PaperWriterProvider } from './PaperWriterContext';
import type { OutlineSection } from './PaperWriterContext';
import {
  TABS_CONFIG,
  ACCEPTED_TYPES,
  QUICK_PROMPTS,
  PAPER_TYPES,
  ACADEMIC_LEVELS,
  CITATION_STYLES,
  WORD_COUNTS,
  LANGUAGES,
  TONES,
  GENERATE_STEPS,
} from './paperWriterConstants';
import type { GeneratedPaper } from './PaperWriterContext';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/feature/Breadcrumb';
import ToolsDropdown from '../../components/feature/ToolsDropdown';
import LoginModal from '../home/components/LoginModal';
import { useFreeTrialGate } from '../../hooks/useFreeTrialGate';
import UpgradeTrialModal from '../../components/feature/UpgradeTrialModal';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'ri-file-pdf-line';
  if (['doc', 'docx'].includes(ext)) return 'ri-file-word-line';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'ri-image-line';
  if (ext === 'csv') return 'ri-file-excel-line';
  if (['md', 'txt'].includes(ext)) return 'ri-file-text-line';
  return 'ri-file-line';
}

// ─── Export Modal ─────────────────────────────────────────────────────────────
function ExportModal({ paper, onClose }: { paper: GeneratedPaper; onClose: () => void }) {
  const [exportStatus, setExportStatus] = useState<'idle' | 'exporting' | 'done'>('idle');
  const [exportTarget, setExportTarget] = useState<'gdocs' | 'notion' | null>(null);

  const fullText = [
    paper.title.toUpperCase(), '',
    'ABSTRACT', paper.abstract, '',
    ...paper.sections.flatMap(s => [s.heading.toUpperCase(), s.content, '']),
    'REFERENCES',
    ...paper.references.map((r, i) => `${i + 1}. ${r}`),
  ].join('\n');

  const handleExport = (target: 'gdocs' | 'notion') => {
    setExportTarget(target);
    setExportStatus('exporting');

    setTimeout(() => {
      if (target === 'gdocs') {
        // Open Google Docs with pre-filled content via data URI approach
        const encoded = encodeURIComponent(fullText);
        const url = `https://docs.google.com/document/create?title=${encodeURIComponent(paper.title)}`;
        window.open(url, '_blank');
      } else {
        // Notion: copy content to clipboard and open Notion
        navigator.clipboard.writeText(fullText).then(() => {
          window.open('https://www.notion.so/new', '_blank');
        });
      }
      setExportStatus('done');
    }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 bg-gradient-to-r from-emerald-600 to-teal-600 border-b border-emerald-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <i className="ri-share-forward-line text-white text-base"></i>
            </div>
            <div>
              <p className="text-sm font-extrabold text-white">Export Paper</p>
              <p className="text-xs text-emerald-100">Send to Google Docs or Notion</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/20 hover:bg-white/30 text-white transition-all cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Paper info */}
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-gray-50 border border-gray-100">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 flex-shrink-0">
              <i className="ri-file-text-line text-emerald-600 text-base"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 line-clamp-2 leading-tight">{paper.title}</p>
              <p className="text-xs text-gray-400 mt-1">{paper.wordCount.toLocaleString()} words · {paper.citationStyle} · {paper.paperType}</p>
            </div>
          </div>

          {/* Export Options */}
          <div className="space-y-3">
            {/* Google Docs */}
            <button
              onClick={() => handleExport('gdocs')}
              disabled={exportStatus === 'exporting'}
              className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all cursor-pointer text-left ${
                exportTarget === 'gdocs' && exportStatus === 'done'
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md'
              } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white border border-gray-200 shadow-sm flex-shrink-0">
                <svg viewBox="0 0 48 48" className="w-7 h-7" fill="none">
                  <path d="M28 4H12C9.8 4 8 5.8 8 8v32c0 2.2 1.8 4 4 4h24c2.2 0 4-1.8 4-4V20L28 4z" fill="#4285F4"/>
                  <path d="M28 4v16h16L28 4z" fill="#A1C2FA"/>
                  <path d="M16 28h16v2H16zm0-6h16v2H16zm0 12h10v2H16z" fill="white"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold text-gray-900">Export to Google Docs</p>
                <p className="text-xs text-gray-500 mt-0.5">Opens a new Google Doc with your paper title pre-filled</p>
              </div>
              {exportTarget === 'gdocs' && exportStatus === 'exporting' && (
                <i className="ri-loader-4-line text-emerald-500 text-lg animate-spin flex-shrink-0"></i>
              )}
              {exportTarget === 'gdocs' && exportStatus === 'done' && (
                <i className="ri-check-circle-fill text-emerald-500 text-lg flex-shrink-0"></i>
              )}
              {exportTarget !== 'gdocs' && (
                <i className="ri-external-link-line text-gray-400 text-base flex-shrink-0"></i>
              )}
            </button>

            {/* Notion */}
            <button
              onClick={() => handleExport('notion')}
              disabled={exportStatus === 'exporting'}
              className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all cursor-pointer text-left ${
                exportTarget === 'notion' && exportStatus === 'done'
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md'
              } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gray-900 flex-shrink-0">
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="white">
                  <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.374.466-.748.933-.748 1.167V20L7.822 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L3.822 9.76c-.094-.42.28-.887.748-.933z"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold text-gray-900">Export to Notion</p>
                <p className="text-xs text-gray-500 mt-0.5">Copies full paper text to clipboard, then opens Notion</p>
              </div>
              {exportTarget === 'notion' && exportStatus === 'exporting' && (
                <i className="ri-loader-4-line text-emerald-500 text-lg animate-spin flex-shrink-0"></i>
              )}
              {exportTarget === 'notion' && exportStatus === 'done' && (
                <i className="ri-check-circle-fill text-emerald-500 text-lg flex-shrink-0"></i>
              )}
              {exportTarget !== 'notion' && (
                <i className="ri-external-link-line text-gray-400 text-base flex-shrink-0"></i>
              )}
            </button>
          </div>

          {/* Status message */}
          {exportStatus === 'done' && exportTarget === 'notion' && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
              <i className="ri-check-circle-fill text-emerald-500 text-base flex-shrink-0 mt-0.5"></i>
              <p className="text-xs text-emerald-700 font-semibold leading-relaxed">
                Paper text copied to clipboard! Paste it into your new Notion page with <strong>Ctrl+V</strong> (or ⌘V on Mac).
              </p>
            </div>
          )}
          {exportStatus === 'done' && exportTarget === 'gdocs' && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
              <i className="ri-check-circle-fill text-emerald-500 text-base flex-shrink-0 mt-0.5"></i>
              <p className="text-xs text-emerald-700 font-semibold leading-relaxed">
                Google Docs opened! Copy your paper content from the preview and paste it into the new document.
              </p>
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Paper History Sidebar ────────────────────────────────────────────────────
function PaperHistorySidebar() {
  const { 
    paperHistory, 
    loadPaperFromHistory, 
    deletePaperFromHistory, 
    clearAllHistory,
    isHistorySidebarOpen,
    setIsHistorySidebarOpen 
  } = usePaperWriter();

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <>
      <button
        onClick={() => setIsHistorySidebarOpen(!isHistorySidebarOpen)}
        className="fixed left-0 top-1/2 -translate-y-1/2 z-50 flex items-center gap-2 px-3 py-4 bg-emerald-600 text-white rounded-r-xl shadow-lg hover:bg-emerald-700 transition-all cursor-pointer group"
        style={{ writingMode: 'vertical-rl' }}
      >
        <i className={`ri-history-line text-lg transition-transform ${isHistorySidebarOpen ? 'rotate-180' : ''}`}></i>
        <span className="text-xs font-bold tracking-wider">HISTORY</span>
      </button>

      <div className={`fixed left-0 top-0 bottom-0 w-80 bg-white border-r border-gray-200 shadow-2xl z-40 transition-transform duration-300 ${isHistorySidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100">
                <i className="ri-history-line text-emerald-600 text-base"></i>
              </div>
              <div>
                <p className="text-sm font-extrabold text-gray-900">Paper History</p>
                <p className="text-xs text-gray-500">{paperHistory.length} saved</p>
              </div>
            </div>
            <button onClick={() => setIsHistorySidebarOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/60 text-gray-500 hover:text-gray-700 transition-all cursor-pointer">
              <i className="ri-close-line text-base"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {paperHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <div className="w-20 h-20 flex items-center justify-center rounded-3xl bg-gradient-to-br from-gray-100 to-gray-200 mb-4">
                  <i className="ri-file-list-3-line text-gray-400 text-3xl"></i>
                </div>
                <p className="text-sm font-bold text-gray-600 mb-1">No History Yet</p>
                <p className="text-xs text-gray-400 leading-relaxed">Generated papers will appear here automatically</p>
              </div>
            ) : (
              paperHistory.map((entry) => (
                <div
                  key={entry.id}
                  className="group relative bg-white border border-gray-200 rounded-xl p-4 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => { loadPaperFromHistory(entry.id); setIsHistorySidebarOpen(false); }}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); deletePaperFromHistory(entry.id); }}
                    className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center rounded-lg bg-white border border-gray-200 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:border-red-300 text-gray-400 hover:text-red-500 transition-all cursor-pointer z-10"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                  <div className="pr-8">
                    <h3 className="text-sm font-bold text-gray-900 mb-2 line-clamp-2 leading-tight">{entry.title}</h3>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{entry.paperType}</span>
                      <span className="text-xs font-medium text-gray-500">{entry.citationStyle}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span className="flex items-center gap-1"><i className="ri-file-text-line"></i>{entry.wordCount.toLocaleString()} words</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>{formatDate(entry.dateGenerated)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {paperHistory.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={clearAllHistory}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-600 text-xs font-bold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all cursor-pointer"
              >
                <i className="ri-delete-bin-line text-sm"></i>
                Clear All History
              </button>
            </div>
          )}
        </div>
      </div>

      {isHistorySidebarOpen && (
        <div className="fixed inset-0 bg-black/20 z-30 transition-opacity" onClick={() => setIsHistorySidebarOpen(false)}></div>
      )}
    </>
  );
}

// ─── Streaming Paper Preview ──────────────────────────────────────────────────
function StreamingPaperPreview() {
  const { streamingState, streamingPaper, generatedPaper, aiProof, resetPaper, isGenerated, setIsComparisonMode } = usePaperWriter();
  const [copied, setCopied] = useState(false);
  const [collapsedSection, setCollapsedSection] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const paper = isGenerated ? generatedPaper : streamingPaper;
  if (!paper) return null;

  const isStreaming = streamingState.isStreaming;
  const canInteract = !isStreaming;

  const fullText = [
    paper.title.toUpperCase(), '',
    'ABSTRACT', paper.abstract, '',
    ...paper.sections.flatMap(s => [s.heading.toUpperCase(), s.content, '']),
    'REFERENCES',
    ...paper.references.map((r, i) => `${i + 1}. ${r}`),
  ].join('\n');

  const handleCopy = () => {
    if (!canInteract) return;
    navigator.clipboard.writeText(fullText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const renderStreamingText = (section: string, fullContent: string) => {
    if (!isStreaming) return fullContent;
    const currentSection = streamingState.currentSection;
    const currentText = streamingState.currentText;
    const showCursor = streamingState.showCursor;
    if (currentSection === section) {
      return (
        <>
          {currentText}
          {showCursor && <span className="inline-block w-0.5 h-4 bg-emerald-500 ml-0.5 animate-pulse">|</span>}
        </>
      );
    }
    const sectionOrder = [
      'title', 'abstract-heading', 'abstract',
      ...paper.sections.flatMap(s => [`section-${s.heading}-heading`, `section-${s.heading}`]),
      'references-heading',
      ...paper.references.map((_, i) => `reference-${i}`),
    ];
    const currentIndex = sectionOrder.indexOf(currentSection);
    const thisIndex = sectionOrder.indexOf(section);
    if (thisIndex < currentIndex) return fullContent;
    return '';
  };

  return (
    <>
      {showExportModal && isGenerated && generatedPaper && (
        <ExportModal paper={generatedPaper} onClose={() => setShowExportModal(false)} />
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <i className="ri-file-text-line text-white text-base"></i>
            </div>
            <div>
              <p className="text-sm font-extrabold text-white leading-none">{isStreaming ? 'Generating Paper...' : 'Generated Paper'}</p>
              <p className="text-xs text-emerald-100 mt-0.5">{paper.wordCount.toLocaleString()} words · {paper.citationStyle} · {paper.paperType}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isStreaming && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 text-white text-xs font-bold border border-white/30">
                <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
                AI is writing…
              </span>
            )}
            {!isStreaming && aiProof.enabled && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 text-white text-xs font-bold border border-white/30">
                <i className="ri-shield-star-fill text-xs"></i>AI-Proof Applied
              </span>
            )}
            {isGenerated && (
              <button
                onClick={() => setIsComparisonMode(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer bg-white/20 text-white hover:bg-white/30"
              >
                <i className="ri-split-cells-horizontal text-sm"></i>Compare Versions
              </button>
            )}
            <button
              onClick={handleCopy}
              disabled={!canInteract}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${!canInteract ? 'bg-white/10 text-white/40 cursor-not-allowed' : copied ? 'bg-white text-emerald-700' : 'bg-white/20 text-white hover:bg-white/30'}`}
            >
              <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}></i>{copied ? 'Copied!' : 'Copy All'}
            </button>
            <button
              onClick={resetPaper}
              disabled={!canInteract}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap ${!canInteract ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-white/20 text-white hover:bg-white/30 cursor-pointer'}`}
            >
              <i className="ri-refresh-line text-sm"></i>Regenerate
            </button>
          </div>
        </div>

        {/* Download + Export Bar */}
        <div className="flex items-center gap-3 px-6 py-3 bg-gray-50 border-b border-gray-100 flex-wrap">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Download:</span>
          <button disabled={!canInteract} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${!canInteract ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer'}`}>
            <i className="ri-file-word-line text-sm"></i>DOCX
          </button>
          <button disabled={!canInteract} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${!canInteract ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-red-500 text-white hover:bg-red-600 cursor-pointer'}`}>
            <i className="ri-file-pdf-line text-sm"></i>PDF
          </button>
          <button disabled={!canInteract} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${!canInteract ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-800 cursor-pointer'}`}>
            <i className="ri-file-text-line text-sm"></i>TXT
          </button>

          {/* Divider */}
          <span className="text-gray-300 text-sm">|</span>
          <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Export:</span>

          {/* Google Docs */}
          <button
            disabled={!canInteract}
            onClick={() => canInteract && setShowExportModal(true)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap border ${
              !canInteract
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-200'
                : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400 hover:text-emerald-700 hover:shadow-sm cursor-pointer'
            }`}
          >
            <svg viewBox="0 0 48 48" className="w-3.5 h-3.5 flex-shrink-0" fill="none">
              <path d="M28 4H12C9.8 4 8 5.8 8 8v32c0 2.2 1.8 4 4 4h24c2.2 0 4-1.8 4-4V20L28 4z" fill="#4285F4"/>
              <path d="M28 4v16h16L28 4z" fill="#A1C2FA"/>
            </svg>
            Google Docs
          </button>

          {/* Notion */}
          <button
            disabled={!canInteract}
            onClick={() => canInteract && setShowExportModal(true)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap border ${
              !canInteract
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-200'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400 hover:text-gray-900 hover:shadow-sm cursor-pointer'
            }`}
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor">
              <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.374.466-.748.933-.748 1.167V20L7.822 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L3.822 9.76c-.094-.42.28-.887.748-.933z"/>
            </svg>
            Notion
          </button>

          <div className="ml-auto flex items-center gap-2 text-xs text-gray-400">
            <i className="ri-time-line text-sm"></i>~{Math.ceil(paper.wordCount / 238)} min read
          </div>
        </div>

        {/* Paper Content */}
        <div className="p-8 max-h-[700px] overflow-y-auto space-y-8 font-serif">
          <div className="text-center border-b border-gray-200 pb-8">
            <h1 className="text-xl font-extrabold text-gray-900 leading-tight mb-3">
              {renderStreamingText('title', paper.title)}
            </h1>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              {[paper.paperType, paper.academicLevel, `${paper.citationStyle} Format`, `${paper.wordCount.toLocaleString()} words`].map(t => (
                <span key={t} className="text-xs font-semibold text-gray-500 px-3 py-1 rounded-full bg-gray-100">{t}</span>
              ))}
            </div>
          </div>

          {paper.tableOfContents.length > 0 && !isStreaming && (
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h2 className="text-sm font-extrabold text-gray-700 uppercase tracking-widest mb-3 flex items-center gap-2">
                <i className="ri-list-ordered text-emerald-500 text-base"></i>Table of Contents
              </h2>
              <ul className="space-y-1.5">
                {paper.tableOfContents.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600 hover:text-emerald-600 cursor-pointer transition-colors">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 flex-shrink-0"></span>{item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h2 className="text-base font-extrabold text-gray-800 uppercase tracking-widest mb-3 pb-2 border-b-2 border-emerald-100">
              {renderStreamingText('abstract-heading', 'ABSTRACT')}
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed italic bg-emerald-50/40 px-4 py-3 rounded-xl border-l-4 border-emerald-300">
              {renderStreamingText('abstract', paper.abstract)}
            </p>
          </div>

          {paper.sections.map((section, i) => {
            const sectionKey = `section-${section.heading}`;
            const headingKey = `${sectionKey}-heading`;
            const headingContent = renderStreamingText(headingKey, section.heading);
            const bodyContent = renderStreamingText(sectionKey, section.content);
            if (!headingContent && isStreaming) return null;
            return (
              <div key={i}>
                <button
                  onClick={() => !isStreaming && setCollapsedSection(collapsedSection === section.heading ? null : section.heading)}
                  disabled={isStreaming}
                  className={`w-full flex items-center justify-between gap-3 mb-3 pb-2 border-b-2 border-gray-100 transition-colors ${isStreaming ? 'cursor-default' : 'hover:border-emerald-200 cursor-pointer group'}`}
                >
                  <h2 className={`text-base font-extrabold text-gray-800 text-left transition-colors ${!isStreaming && 'group-hover:text-emerald-700'}`}>
                    {headingContent}
                  </h2>
                  {!isStreaming && (
                    <i className={`ri-arrow-down-s-line text-gray-400 text-lg transition-transform flex-shrink-0 ${collapsedSection === section.heading ? 'rotate-180' : ''}`}></i>
                  )}
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${collapsedSection === section.heading && !isStreaming ? 'max-h-0 opacity-0' : 'max-h-none opacity-100'}`}>
                  {bodyContent && bodyContent.toString().split('\n\n').map((para, j) => (
                    <p key={j} className="text-sm text-gray-700 leading-relaxed mb-4 last:mb-0">{para}</p>
                  ))}
                </div>
              </div>
            );
          })}

          {paper.references.length > 0 && (
            <div>
              <h2 className="text-base font-extrabold text-gray-800 uppercase tracking-widest mb-4 pb-2 border-b-2 border-gray-100">
                {renderStreamingText('references-heading', 'REFERENCES')}
              </h2>
              <ol className="space-y-3">
                {paper.references.map((ref, i) => {
                  const refContent = renderStreamingText(`reference-${i}`, `${i + 1}. ${ref}`);
                  if (!refContent && isStreaming) return null;
                  return (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed">
                      <span className="text-xs font-bold text-emerald-500 mt-0.5 flex-shrink-0 w-5 text-right">{i + 1}.</span>
                      <span dangerouslySetInnerHTML={{ __html: ref.replace(/\*(.*?)\*/g, '<em>$1</em>') }} />
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Paper Comparison View ────────────────────────────────────────────────────
// NOTE: The duplicate `PaperComparisonView` component previously defined earlier in this file
// has been removed to eliminate the naming conflict. The implementation below remains unchanged.
function PaperComparisonView() {
  const {
    versionA,
    versionB,
    versionBOptions,
    setVersionBOptions,
    isGeneratingVersionB,
    generateVersionB,
    diffHighlightEnabled,
    setDiffHighlightEnabled,
    exitComparisonMode,
  } = usePaperWriter();

  const [copiedA, setCopiedA] = useState(false);
  const [copiedB, setCopiedB] = useState(false);

  const handleCopyA = () => {
    if (!versionA) return;
    const fullText = [
      versionA.paper.title.toUpperCase(), '',
      'ABSTRACT', versionA.paper.abstract, '',
      ...versionA.paper.sections.flatMap(s => [s.heading.toUpperCase(), s.content, '']),
      'REFERENCES',
      ...versionA.paper.references.map((r, i) => `${i + 1}. ${r}`),
    ].join('\n');
    navigator.clipboard.writeText(fullText).then(() => {
      setCopiedA(true);
      setTimeout(() => setCopiedA(false), 2500);
    });
  };

  const handleCopyB = () => {
    if (!versionB) return;
    const fullText = [
      versionB.paper.title.toUpperCase(), '',
      'ABSTRACT', versionB.paper.abstract, '',
      ...versionB.paper.sections.flatMap(s => [s.heading.toUpperCase(), s.content, '']),
      'REFERENCES',
      ...versionB.paper.references.map((r, i) => `${i + 1}. ${r}`),
    ].join('\n');
    navigator.clipboard.writeText(fullText).then(() => {
      setCopiedB(true);
      setTimeout(() => setCopiedB(false), 2500);
    });
  };

  // Calculate stats
  const statsA = versionA ? {
    wordCount: versionA.paper.wordCount,
    sectionCount: versionA.paper.sections.length,
    readingTime: Math.ceil(versionA.paper.wordCount / 238),
  } : null;

  const statsB = versionB ? {
    wordCount: versionB.paper.wordCount,
    sectionCount: versionB.paper.sections.length,
    readingTime: Math.ceil(versionB.paper.wordCount / 238),
  } : null;

  // Simple diff detection (sentence-level)
  const getDiffHighlight = (textA: string, textB: string): boolean => {
    if (!diffHighlightEnabled || !versionB) return false;
    const sentencesA = textA.split(/[.!?]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const sentencesB = textB.split(/[.!?]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    return sentencesA.some(s => !sentencesB.includes(s)) || sentencesB.some(s => !sentencesA.includes(s));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <i className="ri-split-cells-horizontal text-white text-base"></i>
            </div>
            <div>
              <p className="text-sm font-extrabold text-white leading-none">Paper Comparison Mode</p>
              <p className="text-xs text-purple-100 mt-0.5">Compare two versions side-by-side with different settings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDiffHighlightEnabled(!diffHighlightEnabled)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                diffHighlightEnabled
                  ? 'bg-yellow-400 text-gray-900'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <i className="ri-contrast-2-line text-sm"></i>
              {diffHighlightEnabled ? 'Diff: ON' : 'Diff: OFF'}
            </button>
            <button
              onClick={exitComparisonMode}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer bg-white/20 text-white hover:bg-white/30"
            >
              <i className="ri-close-line text-sm"></i>Exit Compare
            </button>
          </div>
        </div>
      </div>

      {/* Stats Comparison Bar */}
      {statsA && statsB && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Statistics Comparison</p>
          <div className="grid grid-cols-3 gap-6">
            {[
              { label: 'Word Count', iconA: 'ri-text', iconB: 'ri-text', valueA: statsA.wordCount.toLocaleString(), valueB: statsB.wordCount.toLocaleString() },
              { label: 'Sections', iconA: 'ri-list-check', iconB: 'ri-list-check', valueA: statsA.sectionCount, valueB: statsB.sectionCount },
              { label: 'Reading Time', iconA: 'ri-time-line', iconB: 'ri-time-line', valueA: `${statsA.readingTime} min`, valueB: `${statsB.readingTime} min` },
            ].map(stat => (
              <div key={stat.label} className="space-y-3">
                <p className="text-xs font-semibold text-gray-600">{stat.label}</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-100 flex-shrink-0">
                      <i className={`${stat.iconA} text-emerald-600 text-sm`}></i>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{stat.valueA}</span>
                  </div>
                  <span className="text-gray-300 text-sm">vs</span>
                  <div className="flex-1 flex items-center gap-2 p-3 rounded-xl bg-purple-50 border border-purple-200">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-purple-100 flex-shrink-0">
                      <i className={`${stat.iconB} text-purple-600 text-sm`}></i>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{stat.valueB}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Side-by-Side Papers */}
      <div className="grid grid-cols-2 gap-6">
        {/* Version A */}
        {versionA && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-emerald-600 to-teal-600">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 flex-shrink-0">
                  <span className="text-white text-xs font-extrabold">A</span>
                </div>
                <div>
                  <p className="text-xs font-extrabold text-white leading-none">Version A</p>
                  <p className="text-xs text-emerald-100 mt-0.5">{versionA.settings.paperType} · {versionA.settings.tone}</p>
                </div>
              </div>
              <button
                onClick={handleCopyA}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  copiedA ? 'bg-white text-emerald-700' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <i className={`${copiedA ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}></i>
                {copiedA ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {/* Settings Pills */}
            <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-emerald-700">{versionA.settings.paperType}</span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-emerald-700">{versionA.settings.tone}</span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-emerald-700">{versionA.settings.citationStyle}</span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-emerald-700">{versionA.settings.wordCount}</span>
            </div>

            {/* Paper Content */}
            <div className="p-6 max-h-[600px] overflow-y-auto space-y-6 font-serif text-sm">
              <div className="text-center pb-4 border-b border-gray-200">
                <h2 className="text-base font-extrabold text-gray-900 leading-tight">{versionA.paper.title}</h2>
              </div>

              <div>
                <h3 className="text-xs font-extrabold text-gray-700 uppercase tracking-widest mb-2">ABSTRACT</h3>
                <p className={`text-xs text-gray-700 leading-relaxed ${getDiffHighlight(versionA.paper.abstract, versionB?.paper.abstract || '') ? 'bg-yellow-100 px-2 py-1 rounded' : ''}`}>
                  {versionA.paper.abstract}
                </p>
              </div>

              {versionA.paper.sections.map((section, i) => {
                const isDiff = versionB && getDiffHighlight(section.content, versionB.paper.sections[i]?.content || '');
                return (
                  <div key={i}>
                    <h3 className="text-xs font-extrabold text-gray-800 mb-2">{section.heading}</h3>
                    {section.content.split('\n\n').map((para, j) => (
                      <p key={j} className={`text-xs text-gray-700 leading-relaxed mb-3 last:mb-0 ${isDiff ? 'bg-yellow-100 px-2 py-1 rounded' : ''}`}>
                        {para}
                      </p>
                    ))}
                  </div>
                );
              })}

              <div>
                <h3 className="text-xs font-extrabold text-gray-700 uppercase tracking-widest mb-2">REFERENCES</h3>
                <ol className="space-y-2">
                  {versionA.paper.references.map((ref, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed">
                      <span className="text-xs font-bold text-emerald-500 flex-shrink-0">{i + 1}.</span>
                      <span dangerouslySetInnerHTML={{ __html: ref.replace(/\*(.*?)\*/g, '<em>$1</em>') }} />
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* Version B */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-purple-600 to-indigo-600">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 flex-shrink-0">
                <span className="text-white text-xs font-extrabold">B</span>
              </div>
              <div>
                <p className="text-xs font-extrabold text-white leading-none">Version B</p>
                {versionB && <p className="text-xs text-purple-100 mt-0.5">{versionB.settings.paperType} · {versionB.settings.tone}</p>}
              </div>
            </div>
            {versionB && (
              <button
                onClick={handleCopyB}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  copiedB ? 'bg-white text-purple-700' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <i className={`${copiedB ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}></i>
                {copiedB ? 'Copied!' : 'Copy'}
              </button>
            )}
          </div>

          {!versionB ? (
            <div className="p-6">
              {/* Settings Panel */}
              <div className="space-y-4 mb-6">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Configure Version B Settings</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Paper Type</label>
                    <select
                      value={versionBOptions.paperType}
                      onChange={e => setVersionBOptions({ ...versionBOptions, paperType: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all cursor-pointer"
                    >
                      {PAPER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tone</label>
                    <select
                      value={versionBOptions.tone}
                      onChange={e => setVersionBOptions({ ...versionBOptions, tone: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all cursor-pointer"
                    >
                      {TONES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Citation Style</label>
                    <select
                      value={versionBOptions.citationStyle}
                      onChange={e => setVersionBOptions({ ...versionBOptions, citationStyle: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all cursor-pointer"
                    >
                      {CITATION_STYLES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Word Count</label>
                    <select
                      value={versionBOptions.wordCount}
                      onChange={e => setVersionBOptions({ ...versionBOptions, wordCount: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all cursor-pointer"
                    >
                      {WORD_COUNTS.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateVersionB}
                disabled={isGeneratingVersionB}
                className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-bold rounded-xl hover:shadow-xl transition-all whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-purple-200"
              >
                {isGeneratingVersionB ? (
                  <>
                    <i className="ri-loader-4-line text-base animate-spin"></i>
                    Generating Version B...
                  </>
                ) : (
                  <>
                    <i className="ri-magic-line text-base"></i>
                    Generate Version B
                  </>
                )}
              </button>

              {/* Info */}
              <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-purple-50 border border-purple-200">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-100 flex-shrink-0">
                  <i className="ri-information-line text-purple-600 text-sm"></i>
                </div>
                <p className="text-xs text-purple-700 leading-relaxed">
                  Adjust the settings above to generate a different version of your paper. You can compare writing styles, citation formats, and tones side-by-side.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Settings Pills */}
              <div className="px-5 py-3 bg-purple-50 border-b border-purple-100 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-purple-200 text-purple-700">{versionB.settings.paperType}</span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-purple-200 text-purple-700">{versionB.settings.tone}</span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-purple-200 text-purple-700">{versionB.settings.citationStyle}</span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-purple-200 text-purple-700">{versionB.settings.wordCount}</span>
              </div>

              {/* Paper Content */}
              <div className="p-6 max-h-[600px] overflow-y-auto space-y-6 font-serif text-sm">
                <div className="text-center pb-4 border-b border-gray-200">
                  <h2 className="text-base font-extrabold text-gray-900 leading-tight">{versionB.paper.title}</h2>
                </div>

                <div>
                  <h3 className="text-xs font-extrabold text-gray-700 uppercase tracking-widest mb-2">ABSTRACT</h3>
                  <p className={`text-xs text-gray-700 leading-relaxed ${getDiffHighlight(versionB.paper.abstract, versionA?.paper.abstract || '') ? 'bg-yellow-100 px-2 py-1 rounded' : ''}`}>
                    {versionB.paper.abstract}
                  </p>
                </div>

                {versionB.paper.sections.map((section, i) => {
                  const isDiff = versionA && getDiffHighlight(section.content, versionA.paper.sections[i]?.content || '');
                  return (
                    <div key={i}>
                      <h3 className="text-xs font-extrabold text-gray-800 mb-2">{section.heading}</h3>
                      {section.content.split('\n\n').map((para, j) => (
                        <p key={j} className={`text-xs text-gray-700 leading-relaxed mb-3 last:mb-0 ${isDiff ? 'bg-yellow-100 px-2 py-1 rounded' : ''}`}>
                          {para}
                        </p>
                      ))}
                    </div>
                  );
                })}

                <div>
                  <h3 className="text-xs font-extrabold text-gray-700 uppercase tracking-widest mb-2">REFERENCES</h3>
                  <ol className="space-y-2">
                    {versionB.paper.references.map((ref, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed">
                        <span className="text-xs font-bold text-purple-500 flex-shrink-0">{i + 1}.</span>
                        <span dangerouslySetInnerHTML={{ __html: ref.replace(/\*(.*?)\*/g, '<em>$1</em>') }} />
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Upload Tab ───────────────────────────────────────────────────────────────
function UploadTab() {
  const { files, handleFiles, removeFile, isDragging, setIsDragging, topicDescription, setTopicDescription } = usePaperWriter();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  }, [handleFiles, setIsDragging]);

  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);

  return (
    <div className="space-y-6">
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-4 p-10 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${isDragging ? 'border-emerald-400 bg-emerald-50 scale-[1.01]' : 'border-gray-200 bg-gray-50 hover:border-emerald-300 hover:bg-emerald-50/40'}`}
      >
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => e.target.files && handleFiles(e.target.files)} />
        <div className={`w-16 h-16 flex items-center justify-center rounded-2xl transition-all ${isDragging ? 'bg-emerald-100' : 'bg-white border border-gray-200'}`}>
          <i className={`ri-upload-cloud-2-line text-3xl transition-colors ${isDragging ? 'text-emerald-500' : 'text-gray-400'}`}></i>
        </div>
        <div className="text-center">
          <p className="text-sm font-bold text-gray-700 mb-1">{isDragging ? 'Drop files here!' : 'Drag & drop files or click to browse'}</p>
          <p className="text-xs text-gray-400">Supports: {ACCEPTED_TYPES.join(' · ')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-center">
          {ACCEPTED_TYPES.map(t => (
            <span key={t} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-gray-200 text-gray-500">{t}</span>
          ))}
        </div>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">{files.length} file{files.length > 1 ? 's' : ''} uploaded</p>
          {files.map(f => (
            <div key={f.id} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-50 flex-shrink-0">
                <i className={`${getFileIcon(f.name)} text-emerald-600 text-base`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{f.name}</p>
                <p className="text-xs text-gray-400">{formatBytes(f.size)}</p>
              </div>
              <button onClick={() => removeFile(f.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
          <i className="ri-edit-line text-emerald-500"></i>
          Topic / Research Question
          <span className="text-xs font-normal text-gray-400">(required if no files)</span>
        </label>
        <textarea
          value={topicDescription}
          onChange={e => { if (e.target.value.length <= 500) setTopicDescription(e.target.value); }}
          placeholder="Describe your paper topic, research question, or thesis statement…"
          rows={4}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 transition-all resize-none leading-relaxed"
        />
        <div className="flex items-center justify-between mt-1.5">
          <p className="text-xs text-gray-400">{topicDescription.length}/500 characters</p>
        </div>
      </div>

      <div>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Quick Prompts</p>
        <div className="flex flex-wrap gap-2">
          {QUICK_PROMPTS.map(p => (
            <button
              key={p}
              onClick={() => setTopicDescription(p)}
              className="text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-600 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50 transition-all cursor-pointer whitespace-nowrap"
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Options Tab ──────────────────────────────────────────────────────────────
function OptionsTab() {
  const { paperOptions, setPaperOptions } = usePaperWriter();
  const update = (patch: Partial<typeof paperOptions>) => setPaperOptions({ ...paperOptions, ...patch });

  const SelectField = ({ label, icon, value, options, onChange }: {
    label: string; icon: string; value: string; options: string[]; onChange: (v: string) => void;
  }) => (
    <div>
      <label className="block text-xs font-bold text-gray-600 mb-1.5 flex items-center gap-1.5">
        <i className={`${icon} text-emerald-500 text-sm`}></i>{label}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 transition-all cursor-pointer"
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <SelectField label="Paper Type" icon="ri-file-text-line" value={paperOptions.paperType} options={PAPER_TYPES} onChange={v => update({ paperType: v })} />
        <SelectField label="Academic Level" icon="ri-graduation-cap-line" value={paperOptions.academicLevel} options={ACADEMIC_LEVELS} onChange={v => update({ academicLevel: v })} />
        <SelectField label="Citation Style" icon="ri-double-quotes-l" value={paperOptions.citationStyle} options={CITATION_STYLES} onChange={v => update({ citationStyle: v })} />
        <SelectField label="Word Count" icon="ri-text" value={paperOptions.wordCount} options={WORD_COUNTS} onChange={v => update({ wordCount: v })} />
        <SelectField label="Language" icon="ri-translate-2" value={paperOptions.language} options={LANGUAGES} onChange={v => update({ language: v })} />
        <SelectField label="Tone" icon="ri-emotion-line" value={paperOptions.tone} options={TONES} onChange={v => update({ tone: v })} />
      </div>

      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 space-y-3">
        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Include Sections</p>
        {[
          { key: 'includeAbstract', label: 'Abstract', icon: 'ri-file-list-line', desc: 'A concise summary of the paper' },
          { key: 'includeReferences', label: 'References', icon: 'ri-book-2-line', desc: 'Full bibliography at the end' },
          { key: 'includeTableOfContents', label: 'Table of Contents', icon: 'ri-list-ordered', desc: 'Navigable section outline' },
        ].map(({ key, label, icon, desc }) => (
          <label key={key} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 cursor-pointer hover:border-emerald-200 transition-all">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 flex-shrink-0">
              <i className={`${icon} text-emerald-600 text-sm`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">{label}</p>
              <p className="text-xs text-gray-400">{desc}</p>
            </div>
            <div
              onClick={() => update({ [key]: !paperOptions[key as keyof typeof paperOptions] } as Partial<typeof paperOptions>)}
              className={`relative w-10 h-6 rounded-full transition-all flex-shrink-0 ${paperOptions[key as keyof typeof paperOptions] ? 'bg-emerald-500' : 'bg-gray-200'}`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${paperOptions[key as keyof typeof paperOptions] ? 'left-7' : 'left-1'}`}></div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── AI-Proof Tab ─────────────────────────────────────────────────────────────
function AIProofTab() {
  const { aiProof, setAiProof } = usePaperWriter();
  const update = (patch: Partial<typeof aiProof>) => setAiProof({ ...aiProof, ...patch });

  const INTENSITIES: { value: 'light' | 'medium' | 'aggressive'; label: string; desc: string; color: string; bg: string }[] = [
    { value: 'light', label: 'Light', desc: 'Subtle rewrites, preserves original voice', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-300' },
    { value: 'medium', label: 'Medium', desc: 'Balanced humanization, recommended', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-300' },
    { value: 'aggressive', label: 'Aggressive', desc: 'Maximum bypass, heavy restructuring', color: 'text-red-700', bg: 'bg-red-50 border-red-300' },
  ];

  const TECHNIQUES = [
    { key: 'sentenceVariation', label: 'Sentence Variation', icon: 'ri-shuffle-line', desc: 'Vary sentence length and structure' },
    { key: 'vocabularyDiversification', label: 'Vocabulary Diversification', icon: 'ri-book-open-line', desc: 'Replace repetitive words with synonyms' },
    { key: 'burstinessControl', label: 'Burstiness Control', icon: 'ri-pulse-line', desc: 'Mix short and long sentences naturally' },
    { key: 'perplexityBoost', label: 'Perplexity Boost', icon: 'ri-brain-line', desc: 'Increase text unpredictability' },
    { key: 'idiomInjection', label: 'Idiom Injection', icon: 'ri-chat-quote-line', desc: 'Add natural human expressions' },
    { key: 'microToneShifts', label: 'Micro Tone Shifts', icon: 'ri-equalizer-line', desc: 'Subtle emotional tone variations' },
  ] as const;

  return (
    <div className="space-y-6">
      <div className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${aiProof.enabled ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
        <div className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 ${aiProof.enabled ? 'bg-emerald-100' : 'bg-gray-100'}`}>
          <i className={`ri-shield-star-line text-xl ${aiProof.enabled ? 'text-emerald-600' : 'text-gray-400'}`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-gray-900">AI-Proof Humanization</p>
          <p className="text-xs text-gray-500">Bypass Turnitin, GPTZero, Copyleaks &amp; Originality.ai detection</p>
        </div>
        <div
          onClick={() => update({ enabled: !aiProof.enabled })}
          className={`relative w-12 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 ${aiProof.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
        >
          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${aiProof.enabled ? 'left-7' : 'left-1'}`}></div>
        </div>
      </div>

      {aiProof.enabled && (
        <>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Humanization Intensity</p>
            <div className="grid grid-cols-3 gap-3">
              {INTENSITIES.map(({ value, label, desc, color, bg }) => (
                <button
                  key={value}
                  onClick={() => update({ intensity: value })}
                  className={`flex flex-col items-start gap-1 p-3 rounded-2xl border-2 text-left transition-all cursor-pointer ${aiProof.intensity === value ? bg : 'border-gray-200 bg-white hover:border-gray-300'}`}
                >
                  <span className={`text-sm font-extrabold ${aiProof.intensity === value ? color : 'text-gray-600'}`}>{label}</span>
                  <span className="text-xs text-gray-400 leading-tight">{desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Target Detectors</p>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(aiProof.targets) as (keyof typeof aiProof.targets)[]).map(key => (
                <label key={key} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 cursor-pointer hover:border-emerald-200 transition-all">
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-all flex-shrink-0 ${aiProof.targets[key] ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}
                    onClick={() => update({ targets: { ...aiProof.targets, [key]: !aiProof.targets[key] } })}
                  >
                    {aiProof.targets[key] && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                  <span className="text-sm font-semibold text-gray-700 capitalize">
                    {key === 'gptzero' ? 'GPTZero' : key === 'copyleaks' ? 'Copyleaks' : key === 'originality' ? 'Originality.ai' : 'Turnitin'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Humanization Techniques</p>
            <div className="space-y-2">
              {TECHNIQUES.map(({ key, label, icon, desc }) => (
                <label key={key} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 cursor-pointer hover:border-emerald-200 transition-all">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 flex-shrink-0">
                    <i className={`${icon} text-emerald-600 text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{label}</p>
                    <p className="text-xs text-gray-400">{desc}</p>
                  </div>
                  <div
                    onClick={() => update({ techniques: { ...aiProof.techniques, [key]: !aiProof.techniques[key] } })}
                    className={`relative w-10 h-5 rounded-full transition-all flex-shrink-0 cursor-pointer ${aiProof.techniques[key] ? 'bg-emerald-500' : 'bg-gray-200'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${aiProof.techniques[key] ? 'left-5' : 'left-0.5'}`}></div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {!aiProof.enabled && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0">
            <i className="ri-lightbulb-line text-amber-600 text-base"></i>
          </div>
          <p className="text-xs text-amber-700 leading-relaxed">
            Enable AI-Proof to humanize your generated paper so it bypasses AI detection tools like Turnitin, GPTZero, Copyleaks, and Originality.ai.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Generate Button ──────────────────────────────────────────────────────────
function GenerateButton() {
  const { canGenerate, handleGenerate, isGenerating, isGenerated, resetPaper } = usePaperWriter();
  const { exhausted, consume, remaining, dots, used } = useFreeTrialGate('paper-writer');
  const [showUpgrade, setShowUpgrade] = useState(false);

  if (isGenerated) {
    return (
      <button
        onClick={resetPaper}
        className="w-full flex items-center justify-center gap-2 py-3.5 bg-gray-100 text-gray-700 text-sm font-bold rounded-xl hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
      >
        <i className="ri-refresh-line text-base"></i>
        Generate New Paper
      </button>
    );
  }

  const handleClick = () => {
    if (exhausted) { setShowUpgrade(true); return; }
    consume();
    handleGenerate();
  };

  return (
    <>
      {showUpgrade && (
        <UpgradeTrialModal
          toolName="Paper Writer"
          usedCount={used}
          onSignIn={() => setShowUpgrade(false)}
          onClose={() => setShowUpgrade(false)}
        />
      )}
      <div className="space-y-2">
        {!exhausted ? (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium ${remaining === 1 ? 'bg-amber-50 border border-amber-200 text-amber-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
            <div className="flex gap-1">
              {dots.map((d, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${d.active ? 'bg-gray-300' : remaining === 1 ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              ))}
            </div>
            <span>{remaining} free generation{remaining !== 1 ? 's' : ''} left{remaining === 1 ? ' — last one!' : ''}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 text-violet-800">
            <i className="ri-sparkling-2-fill text-violet-500"></i>
            <span>Free trial complete — upgrade to keep writing</span>
          </div>
        )}
        <button
          onClick={handleClick}
          disabled={(!canGenerate || isGenerating) && !exhausted}
          className={`w-full flex items-center justify-center gap-2 py-4 text-white text-sm font-bold rounded-xl hover:shadow-xl transition-all whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-lg ${
            exhausted
              ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 shadow-violet-200'
              : 'bg-gradient-to-r from-emerald-600 to-teal-600 shadow-emerald-200'
          }`}
        >
          <i className={`${isGenerating ? 'ri-loader-4-line animate-spin' : exhausted ? 'ri-sparkling-2-fill' : 'ri-magic-line'} text-base`}></i>
          {isGenerating ? 'Preparing...' : exhausted ? 'Upgrade to Generate More' : 'Generate Paper with AI'}
        </button>
      </div>
    </>
  );
}

// ─── Inner Page ───────────────────────────────────────────────────────────────
function PaperWriterInner() {
  const {
    activeTab,
    setActiveTab,
    isGenerated,
    showOutlineBuilder,
    setShowOutlineBuilder,
    paperOptions,
    setCustomOutline,
    streamingState,
    streamingPaper,
    isComparisonMode,
  } = usePaperWriter();
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);

  const toolTabs = TABS_CONFIG.filter(t => ['proofread', 'plagiarism', 'citation', 'reference', 'readability'].includes(t.id));
  const setupTabs = TABS_CONFIG.filter(t => ['upload', 'templates', 'options', 'aiproof'].includes(t.id));

  const handleOutlineConfirm = (sections: OutlineSection[]) => {
    setCustomOutline(sections);
    setShowOutlineBuilder(false);
  };

  const handleOutlineCancel = () => {
    setShowOutlineBuilder(false);
  };

  const showPaper = isGenerated || streamingState.isStreaming || streamingPaper;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <PaperHistorySidebar />

      <OutlineBuilderModal
        isOpen={showOutlineBuilder}
        paperType={paperOptions.paperType}
        onConfirm={handleOutlineConfirm}
        onCancel={handleOutlineCancel}
      />

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Breadcrumb />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <i className="ri-file-text-line text-white text-xl"></i>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">Paper Writer</h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 rounded-full">
                      <div className="w-1 h-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 animate-pulse"></div>
                      <span className="text-[10px] font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                        Powered by OpenClaw Algorithm
                      </span>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1.5 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 rounded-full">
                      <i className="ri-database-2-line text-violet-600 text-[10px]"></i>
                      <span className="text-[10px] font-medium text-violet-700">94M+ Academic Sources</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ToolsDropdown isDarkMode={false} />
              <button
                onClick={() => setShowLogin(true)}
                className="text-sm font-semibold px-4 py-2 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="fixed top-[60px] left-0 right-0 z-40">
        <Breadcrumb currentPage="Paper Writer" variant="light" />
      </div>

      {/* Main Content */}
      <div className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 max-w-screen-xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold mb-4">
            <i className="ri-ai-generate text-sm"></i>
            AI-Powered Academic Writing
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900 mb-3 leading-tight">
            Write Any Academic Paper in Minutes
          </h1>
          <p className="text-sm text-gray-500 max-w-xl mx-auto leading-relaxed">
            Upload research files, set your options, and let AI generate a fully structured paper — with citations, references, and AI-proof humanization built in.
          </p>
        </div>

        {isComparisonMode ? (
          <PaperComparisonView />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* ── Left Panel ── */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Setup Tabs */}
                <div className="flex border-b border-gray-100 overflow-x-auto">
                  {setupTabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex flex-col items-center gap-1 py-3 px-2 text-xs font-bold transition-all cursor-pointer min-w-0 ${activeTab === tab.id ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                      <i className={`${tab.icon} text-base`}></i>
                      <span className="whitespace-nowrap truncate w-full text-center">{tab.label}</span>
                    </button>
                  ))}
                </div>
                <div className="p-5">
                  {activeTab === 'upload' && <UploadTab />}
                  {activeTab === 'templates' && <TemplatesTab />}
                  {activeTab === 'options' && <OptionsTab />}
                  {activeTab === 'aiproof' && <AIProofTab />}
                </div>
              </div>

              <GenerateButton />

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: 'ri-file-text-line', value: '50K+', label: 'Papers Generated', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                  { icon: 'ri-shield-check-line', value: '99%', label: 'AI-Proof Rate', color: 'text-teal-600', bg: 'bg-teal-50' },
                  { icon: 'ri-time-line', value: '&lt; 60s', label: 'Generation Time', color: 'text-cyan-600', bg: 'bg-cyan-50' },
                ].map(s => (
                  <div key={s.label} className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-gray-100 text-center">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${s.bg}`}>
                      <i className={`${s.icon} ${s.color} text-sm`}></i>
                    </div>
                    <p className="text-sm font-extrabold text-gray-900" dangerouslySetInnerHTML={{ __html: s.value }}></p>
                    <p className="text-xs text-gray-400 leading-tight">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Right Panel ── */}
            <div className="lg:col-span-2 space-y-6">
              {showPaper ? (
                <StreamingPaperPreview />
              ) : (
                <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center py-20 px-8 text-center">
                  <div className="w-20 h-20 flex items-center justify-center rounded-3xl bg-gradient-to-br from-emerald-100 to-teal-100 mb-5">
                    <i className="ri-quill-pen-line text-emerald-500 text-4xl"></i>
                  </div>
                  <h3 className="text-base font-extrabold text-gray-800 mb-2">Your Paper Will Appear Here</h3>
                  <p className="text-sm text-gray-400 max-w-sm leading-relaxed mb-6">
                    Fill in your topic or upload research files on the left, configure your options, then click <strong>Generate Paper with AI</strong>.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {['Research Paper', 'Essay', 'Thesis', 'Literature Review'].map(t => (
                      <span key={t} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-gray-100 text-gray-500">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Writing Tools */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50">
                    <i className="ri-tools-line text-emerald-600 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-extrabold text-gray-900">Writing Tools</p>
                    <p className="text-xs text-gray-400">Proofread, check plagiarism, format citations &amp; more</p>
                  </div>
                </div>

                <div className="flex overflow-x-auto border-b border-gray-100 scrollbar-hide">
                  {toolTabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-3 text-xs font-bold whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${activeTab === tab.id ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                      <i className={`${tab.icon} text-sm`}></i>
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="p-5">
                  {activeTab === 'proofread' && <ProofreadPanel />}
                  {activeTab === 'plagiarism' && <PlagiarismPanel />}
                  {activeTab === 'citation' && <CitationPanel />}
                  {activeTab === 'reference' && <ReferenceBuilderPanel />}
                  {activeTab === 'readability' && <ReadabilityPanel />}
                  {!toolTabs.find(t => t.id === activeTab) && (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-emerald-50 mb-4">
                        <i className="ri-tools-line text-emerald-400 text-2xl"></i>
                      </div>
                      <p className="text-sm font-bold text-gray-600 mb-1">Select a Writing Tool</p>
                      <p className="text-xs text-gray-400">Choose a tool from the tabs above to get started</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function PaperWriter() {
  return (
    <PaperWriterProvider>
      <PaperWriterInner />
    </PaperWriterProvider>
  );
}